import { Fragment, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Group,
  Modal,
  Paper,
  ScrollArea,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconUserPlus } from '@tabler/icons-react';
import { createFileRoute } from '@tanstack/react-router';
import { useAuth } from '@workos-inc/authkit-react';
import { Organisation, User } from '~api';
import { api } from '~portal/api';
import {
  ConfirmActionModal,
  internalOptions,
  internalOrgOptions,
  InternalRoleSelect,
  UserQuotaDrawer,
  UserRowActions,
  useUserAdminActions,
} from './-user-admin';
import * as styles from './-user-admin.css';

export const Route = createFileRoute('/_protected/admin/users_/staff')({
  component: AdminStaffPage,
});

type OrgGroup = { members: User.Managed[]; invited: User.PendingInvitation[] };

/**
 * @desc Orders the per-org groups by the canonical internal-organisation list, with any
 * unexpected org (an internal role held outside the three internal orgs shouldn't happen, but
 * the table must not swallow it) appended alphabetically after. Pending invitations join their
 * organisation's group, so an org with only invited-not-yet-onboarded staff still renders.
 */
function groupByOrganisation(
  users: User.Managed[],
  invitations: User.PendingInvitation[],
): Array<[string, OrgGroup]> {
  const groups = new Map<string, OrgGroup>();
  const group = (name: string): OrgGroup => {
    const existing = groups.get(name) ?? { members: [], invited: [] };
    groups.set(name, existing);
    return existing;
  };
  for (const user of users) group(user.organisation.name).members.push(user);
  for (const invitation of invitations)
    group(invitation.organisation.name).invited.push(invitation);

  const canonical: string[] = Organisation.internalOrganisations.map((org) => org.name);
  return [...groups.entries()].sort(([a], [b]) => {
    const ai = canonical.indexOf(a);
    const bi = canonical.indexOf(b);
    if (ai !== -1 || bi !== -1)
      return (ai === -1 ? canonical.length : ai) - (bi === -1 ? canonical.length : bi);
    return a.localeCompare(b);
  });
}

/**
 * @desc The Team page: internal (staff) users only, grouped per internal organisation. External
 * product users live on the All-users page.
 */
function AdminStaffPage() {
  const { user: authUser } = useAuth();
  const usersQuery = api.users.list.useQuery();
  const actions = useUserAdminActions(() => usersQuery.refetch());
  const { error, setError, busyMembershipId, changeRole, startAction, onSettled } = actions;
  const [quotaUser, setQuotaUser] = useState<User.Managed | null>(null);

  const invitationsQuery = api.users.listInternalInvitations.useQuery();

  const [inviteOpened, { open: openInvite, close: closeInvite }] = useDisclosure(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<string>(User.roles.InternalUser);
  const [inviteOrg, setInviteOrg] = useState<string>(
    Organisation.internalOrganisations[0].externalId,
  );
  const [inviteError, setInviteError] = useState<string | null>(null);

  // Fresh form every time the modal opens — a failed attempt must not leak into the next one.
  const startInvite = () => {
    setInviteEmail('');
    setInviteRole(User.roles.InternalUser);
    setInviteOrg(Organisation.internalOrganisations[0].externalId);
    setInviteError(null);
    openInvite();
  };

  const inviteMutation = api.users.inviteInternal.useMutation({
    onSuccess: () => {
      closeInvite();
      onSettled();
      invitationsQuery.refetch();
    },
    // The modal stays open on failure with the error shown inside it — closing would hide the
    // error behind the page and discard the form the admin may only need to correct.
    onError: (err) => setInviteError(err.message),
  });

  const internalUsers = (usersQuery.data ?? []).filter((user) => user.internalRole !== null);
  const groups = groupByOrganisation(internalUsers, invitationsQuery.data ?? []);

  return (
    <Stack gap="lg" p="lg">
      <ConfirmActionModal actions={actions} />
      <UserQuotaDrawer user={quotaUser} onClose={() => setQuotaUser(null)} />

      <Modal opened={inviteOpened} onClose={closeInvite} title="Invite internal user" centered>
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            Sends a WorkOS invitation into the chosen internal organisation carrying the chosen
            role. There is no pending state — the invitee has access as soon as they accept.
          </Text>
          {inviteError && (
            <Alert
              color="red"
              title="Invitation failed"
              withCloseButton
              onClose={() => setInviteError(null)}
            >
              {inviteError}
            </Alert>
          )}
          <TextInput
            label="Email"
            type="email"
            value={inviteEmail}
            onChange={(event) => setInviteEmail(event.currentTarget.value)}
            placeholder="colleague@h2gc.com"
            data-autofocus
          />
          <Select
            label="Organisation"
            allowDeselect={false}
            data={internalOrgOptions}
            value={inviteOrg}
            onChange={(value) => value && setInviteOrg(value)}
          />
          <Select
            label="Role"
            allowDeselect={false}
            data={internalOptions}
            value={inviteRole}
            onChange={(value) => value && setInviteRole(value)}
          />
          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={closeInvite}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                setInviteError(null);
                inviteMutation.mutate({
                  email: inviteEmail,
                  role: inviteRole as
                    typeof User.roles.InternalUser | typeof User.roles.InternalAdmin,
                  organisationExternalId: inviteOrg as Organisation.InternalOrganisationExternalId,
                });
              }}
              disabled={!inviteEmail.trim()}
              loading={inviteMutation.isPending}
            >
              Send invitation
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Group justify="space-between">
        <Title order={1}>Staff</Title>
        <Group gap="sm">
          <Button
            onClick={() => {
              usersQuery.refetch();
              invitationsQuery.refetch();
            }}
            loading={usersQuery.isFetching || invitationsQuery.isFetching}
            size="xs"
            variant="light"
          >
            Refresh
          </Button>
          <Button size="xs" leftSection={<IconUserPlus size={16} />} onClick={startInvite}>
            Invite internal user
          </Button>
        </Group>
      </Group>

      {error && (
        <Alert color="red" title="Error" withCloseButton onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper withBorder p="md">
        <Text size="sm" c="dimmed" mb="md">
          Staff across the internal organisations. Role changes take effect on the user&rsquo;s next
          access token.
        </Text>

        {groups.length === 0 && !usersQuery.isLoading ? (
          <Text c="dimmed" size="sm">
            No internal users found
          </Text>
        ) : (
          <ScrollArea>
            <Table striped highlightOnHover miw={680}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Email</Table.Th>
                  <Table.Th>Name</Table.Th>
                  <Table.Th>Role</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>Actions</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {groups.map(([orgName, { members, invited }], groupIndex) => (
                  <Fragment key={orgName}>
                    {groupIndex > 0 && (
                      <Table.Tr aria-hidden className={styles.spacerRow}>
                        <Table.Td colSpan={5} />
                      </Table.Tr>
                    )}
                    <Table.Tr data-testid={`team-org-${orgName}`}>
                      <Table.Td colSpan={5} className={styles.orgHeaderCell}>
                        <Group gap="xs">
                          <Text size="sm" fw={600}>
                            {orgName}
                          </Text>
                          <Badge size="xs" variant="light" color="gray">
                            {members.length}
                          </Badge>
                          {invited.length > 0 && (
                            <Badge size="xs" variant="light" color="yellow">
                              {invited.length} invited
                            </Badge>
                          )}
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                    {members.map((user) => {
                      const isSelf = user.workosUserId === authUser?.id;
                      const busy = busyMembershipId === user.membershipId;

                      return (
                        <Table.Tr key={user.membershipId} data-testid={`user-${user.workosUserId}`}>
                          <Table.Td>
                            <Group gap="xs" wrap="nowrap">
                              <Text size="sm">{user.email || user.workosUserId}</Text>
                              {isSelf && (
                                <Badge size="xs" variant="light">
                                  You
                                </Badge>
                              )}
                            </Group>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm">{user.name ?? '—'}</Text>
                          </Table.Td>
                          <Table.Td>
                            <InternalRoleSelect
                              user={user}
                              isSelf={isSelf}
                              busy={busy}
                              changeRole={changeRole}
                            />
                          </Table.Td>
                          <Table.Td>
                            <Badge
                              color={user.status === 'active' ? 'green' : 'gray'}
                              variant="filled"
                              size="sm"
                            >
                              {user.status}
                            </Badge>
                          </Table.Td>
                          <Table.Td>
                            <UserRowActions
                              user={user}
                              isSelf={isSelf}
                              busy={busy}
                              startAction={startAction}
                              onViewQuota={setQuotaUser}
                              removeWip
                            />
                          </Table.Td>
                        </Table.Tr>
                      );
                    })}
                    {invited.map((invitation) => (
                      <Table.Tr
                        key={invitation.invitationId}
                        data-testid={`invitation-${invitation.invitationId}`}
                      >
                        <Table.Td>
                          <Text size="sm" c="dimmed">
                            {invitation.email}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm" c="dimmed">
                            —
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm" c="dimmed">
                            {internalOptions.find((option) => option.value === invitation.role)
                              ?.label ?? '—'}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Tooltip
                            label={`Invitation sent ${new Date(invitation.createdAt).toLocaleDateString()}, expires ${new Date(invitation.expiresAt).toLocaleDateString()}`}
                          >
                            <Badge color="yellow" variant="light" size="sm">
                              invited
                            </Badge>
                          </Tooltip>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm" c="dimmed">
                            —
                          </Text>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Fragment>
                ))}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        )}
      </Paper>
    </Stack>
  );
}
